import { useCallback } from 'react';

import {
	ContentAndHeaderQueryArtifact,
	type ContentAndHeaderQuery$data,
	type ContentAndHeaderQuery$variables,
} from '@confluence/content-unified-query';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { useSpaceHomepageId } from '@confluence/space-utils/entry-points/useSpaceHomepageId.ts';
import { UseEditorContentModeValueQuery } from '@confluence/editor-content-mode-button/entry-points/UseEditorContentModeValueQuery.graphql.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import {
	SpaceOverviewQuery,
	type SpaceOverviewQuery$data,
	type SpaceOverviewQuery$variables,
} from '@confluence/space-overview-query/entry-points/SpaceOverviewQuery.ts';

export const useForceRefetchContent = (
	contentId: string,
	spaceKey: string,
): (() => Promise<{
	spaceOverviewQueryResponse: any;
	contentAndHeaderQueryResponse: any;
	contentModeQueryResponse: any;
}>) => {
	const homepageId = useSpaceHomepageId(spaceKey);

	return useCallback(async () => {
		let spaceOverviewQueryResponse;
		let contentAndHeaderQueryResponse;
		let contentModeQueryResponse;
		if (contentId === homepageId) {
			try {
				spaceOverviewQueryResponse = await queryInterop<
					SpaceOverviewQuery$data,
					SpaceOverviewQuery$variables
				>({
					query: SpaceOverviewQuery,
					context: { single: true },
					fetchPolicy: 'network-only',
					variables: {
						spaceKey,
						excludeOperations: Boolean(!isCompanyHubSpaceKey(spaceKey)),
					},
				});
			} catch {
				// NOTE: We intentionally swallow any possible error here as this query is only aimed
				// populate the cache with just published content. Should any error occur here after
				// successful publishing the page, the Apollo cache for the query will be invalidated
				// anyway and View Page will fetch the page and handle possible errors.
			}
		}
		if (contentId) {
			try {
				contentAndHeaderQueryResponse = await queryInterop<
					ContentAndHeaderQuery$data,
					ContentAndHeaderQuery$variables
				>({
					query: ContentAndHeaderQueryArtifact,
					context: { single: true },
					fetchPolicy: 'network-only',
					variables: {
						contentId,
						spaceKey,
						useNewContentTopper: true,
					},
				});
			} catch {
				// NOTE: We intentionally swallow any possible error here as this query is only aimed
				// populate the cache with just published content. Should any error occur here after
				// successful publishing the page, the Apollo cache for the query will be invalidated
				// anyway and View Page will fetch the page and handle possible errors.
			}

			try {
				contentModeQueryResponse = await queryInterop({
					query: UseEditorContentModeValueQuery,
					context: { single: true },
					fetchPolicy: 'network-only',
					variables: {
						contentId,
						isDraft: false,
						status: ['current'],
					},
				});
			} catch {
				// NOTE: Content mode query is best-effort; swallow errors to keep refetch resilient.
			}
		}
		return { spaceOverviewQueryResponse, contentAndHeaderQueryResponse, contentModeQueryResponse };
	}, [contentId, spaceKey, homepageId]);
};
